import 'leaflet/dist/leaflet.css';
import './styles/main.css';

import { WorldMap } from './components/map.ts';
import { Timeline, WorldClock } from './components/timeline.ts';
import { AudioPlayer } from './components/player.ts';
import { SubtitleManager } from './components/subtitles.ts';
import { fetchWorldStations, fetchStationsNear, reportStationClick } from './api.ts';
import type { AudioSource, AppState } from './types.ts';

// ─── Application ──────────────────────────────────────────────────────────────

class VoxAetherApp {
  private worldMap: WorldMap;
  private timeline: Timeline;
  private worldClock: WorldClock;
  private player: AudioPlayer;
  private subtitles: SubtitleManager;

  private state: AppState = {
    currentSource: null,
    nearbyStations: [],
    isPlaying: false,
    volume: 0.8,
    timeline: { date: new Date(), minuteOfDay: 0, isLive: true },
    subtitlesEnabled: true,
    discoveryMode: false,
    mapCenter: [20, 0],
    mapZoom: 2,
    sourceFilter: 'all',
  };

  private discoveryTimer: ReturnType<typeof setInterval> | null = null;
  private stationIndex = 0;

  constructor() {
    this.worldMap = new WorldMap('map');
    this.timeline = new Timeline();
    this.worldClock = new WorldClock();
    this.player = new AudioPlayer();
    this.subtitles = new SubtitleManager();

    this.wireUp();
  }

  // ── Boot sequence ──────────────────────────────────────────────────────────

  async init(): Promise<void> {
    try {
      await this.loadWorldStations();
      this.hideLoading();
      await this.playBestNearby(20, 0, /* initialLoad */ true);
    } catch (err) {
      console.error('Init error:', err);
      this.hideLoading();
    }
  }

  // ── Station loading ────────────────────────────────────────────────────────

  private async loadWorldStations(): Promise<void> {
    const sources = await fetchWorldStations(400);
    this.state.nearbyStations = sources;
    this.worldMap.setStations(sources);
  }

  private async loadStationsNear(lat: number, lng: number): Promise<void> {
    const nearby = await fetchStationsNear(lat, lng, 40);
    if (nearby.length > 0) {
      // Merge without duplicates
      const existingIds = new Set(this.state.nearbyStations.map((s) => s.id));
      const fresh = nearby.filter((s) => !existingIds.has(s.id));
      this.state.nearbyStations = [...this.state.nearbyStations, ...fresh];
      this.worldMap.setStations(this.state.nearbyStations);
    }
  }

  // ── Playback helpers ───────────────────────────────────────────────────────

  private async playSource(source: AudioSource): Promise<void> {
    this.state.currentSource = source;
    this.worldMap.setActive(source.id);
    await this.player.play(source);
    void reportStationClick(source.id);
    this.worldClock.updateForLocation(source.lat, source.lng);
  }

  private async playBestNearby(lat: number, lng: number, initialLoad = false): Promise<void> {
    // Filter stations roughly near the viewport centre
    const candidates = this.stationsNear(lat, lng, 30);
    if (candidates.length === 0) {
      if (!initialLoad) return;
      // Fallback: first available station
      const fallback = this.state.nearbyStations[0];
      if (fallback) await this.playSource(fallback);
      return;
    }
    const source = candidates[0];
    await this.playSource(source);
    this.stationIndex = this.state.nearbyStations.indexOf(source);
    this.worldMap.flyTo(source.lat, source.lng, 4);
  }

  /** Return stations sorted by great-circle distance to (lat, lng) */
  private stationsNear(lat: number, lng: number, maxKm: number): AudioSource[] {
    const toRad = (d: number) => (d * Math.PI) / 180;
    return this.state.nearbyStations
      .map((s) => {
        const dLat = toRad(s.lat - lat);
        const dLng = toRad(s.lng - lng);
        const a =
          Math.sin(dLat / 2) ** 2 +
          Math.cos(toRad(lat)) * Math.cos(toRad(s.lat)) * Math.sin(dLng / 2) ** 2;
        const dist = 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return { source: s, dist };
      })
      .filter((x) => x.dist <= maxKm)
      .sort((a, b) => a.dist - b.dist)
      .map((x) => x.source);
  }

  // ── UI wiring ──────────────────────────────────────────────────────────────

  private wireUp(): void {
    // Map click / station select
    this.worldMap.onSelect(async (source) => {
      this.state.discoveryMode = false;
      this.stopDiscovery();
      await this.playSource(source);
    });

    // Map pan/zoom → fetch nearby stations
    this.worldMap.leaflet.on('moveend', () => {
      const c = this.worldMap.getCenter();
      this.state.mapCenter = [c.lat, c.lng];
      void this.loadStationsNear(c.lat, c.lng);
    });

    // Player controls
    this.player.onNext(async () => {
      this.stationIndex = (this.stationIndex + 1) % this.state.nearbyStations.length;
      const next = this.state.nearbyStations[this.stationIndex];
      if (next) {
        await this.playSource(next);
        this.worldMap.flyTo(next.lat, next.lng, 4);
      }
    });

    this.player.onPrev(async () => {
      this.stationIndex =
        (this.stationIndex - 1 + this.state.nearbyStations.length) %
        this.state.nearbyStations.length;
      const prev = this.state.nearbyStations[this.stationIndex];
      if (prev) {
        await this.playSource(prev);
        this.worldMap.flyTo(prev.lat, prev.lng, 4);
      }
    });

    this.player.onPlayPause((playing) => {
      this.state.isPlaying = playing;
    });

    // Timeline changes
    this.timeline.onChange((ts) => {
      this.state.timeline = ts;
    });

    // Discover button
    const discoverBtn = document.getElementById('btn-discover')!;
    discoverBtn.addEventListener('click', () => {
      this.state.discoveryMode = !this.state.discoveryMode;
      discoverBtn.classList.toggle('active', this.state.discoveryMode);
      if (this.state.discoveryMode) this.startDiscovery();
      else this.stopDiscovery();
    });

    // Subtitles toggle
    const subtitlesBtn = document.getElementById('btn-subtitles')!;
    subtitlesBtn.addEventListener('click', () => {
      this.state.subtitlesEnabled = !this.state.subtitlesEnabled;
      subtitlesBtn.classList.toggle('active', this.state.subtitlesEnabled);
      this.subtitles.setEnabled(this.state.subtitlesEnabled);
    });
  }

  // ── Auto-discovery mode ────────────────────────────────────────────────────

  private startDiscovery(): void {
    const jump = async (): Promise<void> => {
      if (this.state.nearbyStations.length === 0) return;
      const idx = Math.floor(Math.random() * this.state.nearbyStations.length);
      const source = this.state.nearbyStations[idx];
      this.stationIndex = idx;
      await this.playSource(source);
      this.worldMap.flyTo(source.lat, source.lng, 4);
    };
    void jump();
    this.discoveryTimer = setInterval(() => void jump(), 45_000);
  }

  private stopDiscovery(): void {
    if (this.discoveryTimer) {
      clearInterval(this.discoveryTimer);
      this.discoveryTimer = null;
    }
  }

  // ── Loading screen ─────────────────────────────────────────────────────────

  private hideLoading(): void {
    const screen = document.getElementById('loading-screen')!;
    screen.classList.add('loading-screen--done');
    setTimeout(() => screen.remove(), 800);
  }
}

// ─── Bootstrap ────────────────────────────────────────────────────────────────

const app = new VoxAetherApp();
void app.init();
